import React, {useEffect, useState} from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHeartbeat} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';

interface VitalReading {
    date: string;
    temp: number | null;
    heart: number | null;
    bp: string | null;
    bg: number | null;
    bmi: number | null;
    resp: number | null;
}

const PatientHealthTracker: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [readings, setReadings] = useState<VitalReading[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Health Tracker - Uzima Healthcare";
        (async () => {
            try {
                const patientId = await localForage.getItem<string>('patient_id');
                if (!patientId) return;
                const token = await localForage.getItem<string>('access_token');
                const body = new URLSearchParams({patient_code: patientId});
                const response = await fetch(`${baseUrl}ehr/patient-records`, {
                    method: 'POST',
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body,
                });
                const data = await response.json();
                if (data.status === 'success') {
                    // Every medical record can carry multiple vitals entries -
                    // flatten to one chronological list, most recent first.
                    const flattened: VitalReading[] = [];
                    for (const record of data.data || []) {
                        for (const vital of record.vitals || []) {
                            flattened.push({date: record.date, ...vital});
                        }
                    }
                    flattened.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    setReadings(flattened);
                }
            } catch (error) {
                console.error('Error fetching vitals:', error);
                Swal.fire('Error', 'Failed to load your health data', 'error');
            } finally {
                setLoading(false);
            }
        })();
    }, [baseUrl]);

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">
                            <FontAwesomeIcon icon={faHeartbeat} className="me-2 text-primary"/>
                            Health Tracker
                        </h2>
                        <p className="text-muted">Vitals recorded at your clinic visits</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : readings.length === 0 ? (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <FontAwesomeIcon icon={faHeartbeat} size="3x" className="text-muted mb-3"/>
                            <h5>No Vitals Recorded Yet</h5>
                            <p className="text-muted">Vitals taken during your clinic visits will show up here.</p>
                        </div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Temp (°C)</th>
                                        <th>Heart Rate</th>
                                        <th>Blood Pressure</th>
                                        <th>Blood Glucose</th>
                                        <th>BMI</th>
                                        <th>Respiratory Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {readings.map((r, i) => (
                                        <tr key={i}>
                                            <td>{new Date(r.date).toLocaleDateString()}</td>
                                            <td>{r.temp ?? '—'}</td>
                                            <td>{r.heart ?? '—'}</td>
                                            <td>{r.bp ?? '—'}</td>
                                            <td>{r.bg ?? '—'}</td>
                                            <td>{r.bmi ?? '—'}</td>
                                            <td>{r.resp ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientHealthTracker;
